import * as yup from 'yup';

export const WHITELABEL_SETTINGS_PRIVACY_POLICY_MESSAGE =
  'Select VTS, whitelabel, or both policies to continue';

export const WHITELABEL_SECONDARY_PRIVACY_URL_REQUIRED =
  'Whitelabel privacy policy URL is required.';

export const WHITELABEL_SECONDARY_TERMS_URL_REQUIRED =
  'Whitelabel terms and conditions URL is required.';

/** Plain object `settings` JSON from the form; avoids Reflect.get / type assertions. */
function isSettingsRecord(s: unknown): s is Record<string, unknown> {
  return typeof s === 'object' && s !== null && !Array.isArray(s);
}

function urlAtIndex(urls: unknown, index: number): unknown {
  return Array.isArray(urls) ? urls[index] : undefined;
}

/**
 * Validates `whitelabel.settings` JSON: at least one of VTS or whitelabel
 * policy links must remain enabled.
 * Defaults: VTS shown when unset (`!== false`); whitelabel shown only when
 * `showWhitelabelPrivacyAndTerms === true` (unset = off).
 */
const validateWhitelabelSettings = yup
  .object()
  .nullable()
  .test(
    'privacy-policy-configuration',
    WHITELABEL_SETTINGS_PRIVACY_POLICY_MESSAGE,
    function (settings: unknown) {
      if (!isSettingsRecord(settings)) {
        return true;
      }

      const showVTS = settings.showVTSPrivacyAndTerms !== false;
      const showWhitelabel = settings.showWhitelabelPrivacyAndTerms === true;

      if (showVTS || showWhitelabel) {
        return true;
      }

      return this.createError({
        path: 'settings',
        message: WHITELABEL_SETTINGS_PRIVACY_POLICY_MESSAGE,
      });
    }
  );

export default validateWhitelabelSettings;

/**
 * Extends the base whitelabel schema (from lane-shared) with validation that
 * at least one of VTS or whitelabel privacy/terms toggles stays enabled.
 */
export function createWhitelabelPrivacyValidator(
  validateWhitelabelBaseSchema: yup.ObjectSchema<any>
) {
  return validateWhitelabelBaseSchema
    .concat(
      yup.object().shape({
        settings: validateWhitelabelSettings,
      })
    )
    .test(
      'whitelabel-secondary-privacy-url-required',
      WHITELABEL_SECONDARY_PRIVACY_URL_REQUIRED,
      function (value: Record<string, unknown> | undefined) {
        const settings = value?.settings;

        if (!isSettingsRecord(settings)) {
          return true;
        }

        if (settings.showWhitelabelPrivacyAndTerms !== true) {
          return true;
        }

        const url = urlAtIndex(value?.privacyPolicyUrls, 1);

        if (!url || String(url).trim() === '') {
          return this.createError({
            path: 'privacyPolicyUrls[1]',
            message: WHITELABEL_SECONDARY_PRIVACY_URL_REQUIRED,
          });
        }

        return true;
      }
    )
    .test(
      'whitelabel-secondary-terms-url-required',
      WHITELABEL_SECONDARY_TERMS_URL_REQUIRED,
      function (value: Record<string, unknown> | undefined) {
        const settings = value?.settings;

        if (!isSettingsRecord(settings)) {
          return true;
        }

        if (settings.showWhitelabelPrivacyAndTerms !== true) {
          return true;
        }

        const url = urlAtIndex(value?.termsAndConditionsUrls, 1);

        if (!url || String(url).trim() === '') {
          return this.createError({
            path: 'termsAndConditionsUrls[1]',
            message: WHITELABEL_SECONDARY_TERMS_URL_REQUIRED,
          });
        }

        return true;
      }
    );
}
